import React, { useState } from "react";
import { Sidebar } from "../components/Sidebar";
import {
  Search,
  Settings,
  Bell,
  Database,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  Plus,
  RefreshCw,
  Filter,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Globe,
  FileText,
  TestTube,
  Eye,
  EyeOff,
  Menu,
} from "lucide-react";
import { Button } from "../components/ui/button";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { useNavigate } from "@tanstack/react-router";
import { cn } from "../lib/utils";

// Removed navigation props since TanStack Router handles navigation

interface DataSource {
  id: string;
  name: string;
  description: string;
  type: "database" | "api" | "file" | "stream" | "cache";
  status: "connected" | "disconnected" | "error" | "syncing";
  lastSync: Date;
  size: string;
  records: number;
  agents: string[];
  url?: string;
  credentials: "configured" | "missing" | "expired";
  databaseType?:
    | "postgresql"
    | "mysql"
    | "redis"
    | "mongodb"
    | "elasticsearch"
    | "snowflake"
    | "kafka"
    | "rabbitmq"
    | "influxdb"
    | "s3"
    | "gcs"
    | "azure";
}

interface ConnectorType {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  category: "database" | "api" | "file" | "stream" | "cache";
  databaseType?: string;
}

interface ConnectionForm {
  name: string;
  description: string;
  host: string;
  port: string;
  database: string;
  username: string;
  password: string;
  ssl: boolean;
}

// Utility to format time difference as 'X minutes ago', 'X hours ago', etc.
function formatRelativeTime(date: Date, now: Date) {
  const diff = now.getTime() - date.getTime();
  const absDiff = Math.abs(diff);

  // If the difference is very small (less than 1 minute), show "Real-time"
  if (absDiff < 60 * 1000) return "Real-time";
  if (absDiff < 60 * 60 * 1000) {
    const mins = Math.round(absDiff / (60 * 1000));
    return `${mins} minute${mins > 1 ? "s" : ""} ago`;
  }
  if (absDiff < 24 * 60 * 60 * 1000) {
    const hours = Math.round(absDiff / (60 * 60 * 1000));
    return `${hours} hour${hours > 1 ? "s" : ""} ago`;
  }
  const days = Math.round(absDiff / (24 * 60 * 60 * 1000));
  return `${days} day${days > 1 ? "s" : ""} ago`;
}

const now = new Date();
const minutesAgo = (n: number) => new Date(now.getTime() - n * 60 * 1000);
const hoursAgo = (n: number) => new Date(now.getTime() - n * 60 * 60 * 1000);
const daysAgo = (n: number) =>
  new Date(now.getTime() - n * 24 * 60 * 60 * 1000);

const sampleDataSources: DataSource[] = [
  {
    id: "1",
    name: "Customer Database",
    description: "Primary customer data for support bot training and testing",
    type: "database",
    status: "connected",
    lastSync: minutesAgo(5),
    size: "2.3 GB",
    records: 15420,
    agents: ["Beauty Sales Agent", "Recommendation Engine"],
    url: "postgresql://customer-db.agenttest.com",
    credentials: "configured",
    databaseType: "postgresql",
  },
  {
    id: "2",
    name: "Product Catalog API",
    description: "Real-time product information and inventory data",
    type: "api",
    status: "connected",
    lastSync: minutesAgo(1),
    size: "156 MB",
    records: 2340,
    agents: ["Recommendation Engine", "Data Analysis Agent"],
    url: "https://api.products.agenttest.com",
    credentials: "configured",
  },
  {
    id: "9",
    name: "Document Storage",
    description: "File storage for documents and media files",
    type: "file",
    status: "connected",
    lastSync: minutesAgo(5),
    size: "234.5 GB",
    records: 45678,
    agents: ["File Agent", "Content Moderation Bot"],
    credentials: "configured",
    databaseType: "s3",
  },
];

const connectorTypes: ConnectorType[] = [
  {
    id: "postgresql",
    name: "PostgreSQL",
    description: "Connect to PostgreSQL database",
    icon: <img src="/postgresql.svg" alt="PostgreSQL" className="h-8 w-8" />,
    category: "database",
    databaseType: "postgresql",
  },
  {
    id: "mysql",
    name: "MySQL",
    description: "Connect to MySQL database",
    icon: <Database className="h-8 w-8 text-white" />,
    category: "database",
    databaseType: "mysql",
  },
  {
    id: "redis",
    name: "Redis",
    description: "Connect to Redis cache",
    icon: <img src="/redis.svg" alt="Redis" className="h-8 w-8" />,
    category: "cache",
    databaseType: "redis",
  },
  {
    id: "mongodb",
    name: "MongoDB",
    description: "Connect to MongoDB database",
    icon: <img src="/mongodb.svg" alt="MongoDB" className="h-8 w-8" />,
    category: "database",
    databaseType: "mongodb",
  },
  {
    id: "elasticsearch",
    name: "Elasticsearch",
    description: "Connect to Elasticsearch index",
    icon: (
      <img src="/elasticsearch.svg" alt="Elasticsearch" className="h-8 w-8" />
    ),
    category: "cache",
    databaseType: "elasticsearch",
  },
  {
    id: "snowflake",
    name: "Snowflake",
    description: "Connect to Snowflake data warehouse",
    icon: (
      <img src="/snowflake-color.svg" alt="Snowflake" className="h-8 w-8" />
    ),
    category: "database",
    databaseType: "snowflake",
  },
  {
    id: "s3",
    name: "Amazon S3",
    description: "Connect to S3 bucket",
    icon: <img src="/s3.svg" alt="S3" className="h-8 w-8" />,
    category: "file",
    databaseType: "s3",
  },
  {
    id: "api",
    name: "REST API",
    description: "Connect to REST API endpoint",
    icon: <Globe className="h-8 w-8 text-white" />,
    category: "api",
  },
];

const DataSourceRow: React.FC<{ dataSource: DataSource }> = ({
  dataSource,
}) => {
  const navigate = useNavigate();
  const getStatusColor = (status: string) => {
    switch (status) {
      case "connected":
        return "bg-pink-900/30 text-pink-400 border-pink-600/30";
      case "disconnected":
        return "bg-gray-900/30 text-gray-400 border-gray-600/30";
      case "error":
        return "bg-red-900/30 text-red-400 border-red-600/30";
      case "syncing":
        return "bg-blue-900/30 text-blue-400 border-blue-600/30";
      default:
        return "bg-gray-900/30 text-gray-400 border-gray-600/30";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "connected":
        return <CheckCircle className="h-4 w-4" />;
      case "disconnected":
        return <XCircle className="h-4 w-4" />;
      case "error":
        return <AlertTriangle className="h-4 w-4" />;
      case "syncing":
        return <RefreshCw className="h-4 w-4 animate-spin" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case "database":
        return <Database className="h-6 w-6 text-white" />;
      case "api":
        return <Globe className="h-6 w-6 text-white" />;
      case "file":
        return <FileText className="h-6 w-6 text-white" />;
      default:
        return <Database className="h-6 w-6 text-white" />;
    }
  };

  const getDatabaseTypeIcon = (databaseType?: string) => {
    switch (databaseType) {
      case "postgresql":
        return (
          <img src="/postgresql.svg" alt="PostgreSQL" className="h-6 w-6" />
        );
      case "redis":
        return <img src="/redis.svg" alt="Redis" className="h-6 w-6" />;
      case "mongodb":
        return <img src="/mongodb.svg" alt="MongoDB" className="h-6 w-6" />;
      case "elasticsearch":
        return (
          <img
            src="/elasticsearch.svg"
            alt="Elasticsearch"
            className="h-6 w-6"
          />
        );
      case "snowflake":
        return (
          <img src="/snowflake-color.svg" alt="Snowflake" className="h-6 w-6" />
        );
      case "s3":
        return <img src="/s3.svg" alt="S3" className="h-6 w-6" />;
      default:
        return getTypeIcon(dataSource.type);
    }
  };

  const getDatabaseTypeLabel = (databaseType?: string) => {
    switch (databaseType) {
      case "postgresql":
        return "PostgreSQL";
      case "mysql":
        return "MySQL";
      case "redis":
        return "Redis";
      case "mongodb":
        return "MongoDB";
      case "elasticsearch":
        return "Elasticsearch";
      case "snowflake":
        return "Snowflake";
      case "kafka":
        return "Kafka";
      case "rabbitmq":
        return "RabbitMQ";
      case "influxdb":
        return "InfluxDB";
      case "s3":
        return "S3";
      case "gcs":
        return "GCS";
      case "azure":
        return "Azure";
      default:
        return null;
    }
  };

  return (
    <div
      className="flex items-center justify-between p-4 bg-gray-800 border border-gray-700 rounded-lg hover:bg-gray-750 transition-colors cursor-pointer"
      onClick={() =>
        navigate({
          to: "/data-sources/$dataSourceId",
          params: { dataSourceId: dataSource.id },
        })
      }
    >
      <div className="flex items-center space-x-4 flex-1">
        {/* Database Type Icon */}
        <div className="flex items-center justify-center w-8 h-8 bg-gray-700 rounded-lg">
          {getDatabaseTypeIcon(dataSource.databaseType)}
        </div>

        {/* Name and Details */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2">
            <h3 className="text-sm font-medium text-gray-100 truncate">
              {dataSource.name}
            </h3>
            <div className="flex items-center space-x-1 text-xs text-gray-400">
              <span>{dataSource.description}</span>
            </div>
          </div>
        </div>

        {/* Records and Size */}
        <div className="flex items-center space-x-2 text-xs text-gray-400">
          <span>{dataSource.size}</span>
        </div>

        {/* Last Sync */}
        <div className="text-xs text-gray-400 min-w-0">
          {formatRelativeTime(dataSource.lastSync, now)}
        </div>

        {/* Actions */}
        <div className="flex items-center space-x-2">
          <Button
            size="sm"
            variant="outline"
            className="border-gray-600 text-gray-300 hover:bg-gray-700"
            onClick={(e) => {
              e.stopPropagation();
              // Handle refresh action
            }}
          >
            <RefreshCw className="h-3 w-3" />
          </Button>
        </div>
      </div>
    </div>
  );
};

interface AddSourceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (dataSource: DataSource) => void;
}

const AddSourceModal: React.FC<AddSourceModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [step, setStep] = useState(1);
  const [selectedConnector, setSelectedConnector] =
    useState<ConnectorType | null>(null);
  const [form, setForm] = useState<ConnectionForm>({
    name: "",
    description: "",
    host: "",
    port: "",
    database: "",
    username: "",
    password: "",
    ssl: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<"success" | "error" | null>(
    null,
  );
  const [testMessage, setTestMessage] = useState("");

  const resetModal = () => {
    setStep(1);
    setSelectedConnector(null);
    setForm({
      name: "",
      description: "",
      host: "",
      port: "",
      database: "",
      username: "",
      password: "",
      ssl: false,
    });
    setShowPassword(false);
    setIsTesting(false);
    setTestResult(null);
    setTestMessage("");
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  const handleConnectorSelect = (connector: ConnectorType) => {
    setSelectedConnector(connector);
    setStep(2);
  };

  const handleTestConnection = async () => {
    setIsTesting(true);
    setTestResult(null);
    setTestMessage("");

    // Simulate connection test
    await new Promise((resolve) => setTimeout(resolve, 2000));

    // Simulate success/failure based on form data
    if (form.host && form.username && form.password) {
      setTestResult("success");
      setTestMessage("Connection successful! All credentials are valid.");
    } else {
      setTestResult("error");
      setTestMessage("Connection failed. Please check your credentials.");
    }

    setIsTesting(false);
  };

  const handleFinish = () => {
    if (selectedConnector && form.name) {
      const newDataSource: DataSource = {
        id: Date.now().toString(),
        name: form.name,
        description: form.description,
        type: selectedConnector.category as any,
        status: "connected",
        lastSync: new Date(),
        size: "0 MB",
        records: 0,
        agents: [],
        url: `${selectedConnector.databaseType}://${form.host}:${form.port}/${form.database}`,
        credentials: "configured",
        databaseType: selectedConnector.databaseType as any,
      };
      onSuccess(newDataSource);
      handleClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-gray-800 border-gray-700 text-gray-100 max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-gray-100">
            Add Data Source
          </DialogTitle>
        </DialogHeader>

        {/* Progress Steps */}
        <div className="px-6 py-4 border-b border-gray-700">
          {/* Circles and lines */}
          <div className="flex items-center justify-center">
            {[1, 2, 3, 4].map((stepNumber, idx, arr) => (
              <React.Fragment key={stepNumber}>
                <div className="flex flex-col items-center">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      step >= stepNumber
                        ? "bg-pink-400 text-white"
                        : "bg-gray-700 text-gray-400"
                    }`}
                  >
                    {stepNumber}
                  </div>
                </div>
                {idx < arr.length - 1 && (
                  <div
                    className={`w-12 h-0.5 mx-2 ${
                      step > stepNumber ? "bg-pink-400" : "bg-gray-700"
                    }`}
                    style={{ alignSelf: "center" }}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
          {/* Labels */}
          <div className="flex items-center justify-center mt-2 gap-x-12">
            <span className="text-xs text-gray-400 w-16 text-center">
              Choose Type
            </span>
            <span className="text-xs text-gray-400 w-16 text-center">
              Credentials
            </span>
            <span className="text-xs text-gray-400 w-16 text-center">Test</span>
            <span className="text-xs text-gray-400 w-16 text-center">Done</span>
          </div>
        </div>

        {/* Step 1: Choose Connector Type */}
        {step === 1 && (
          <div className="p-6">
            <div className="grid grid-cols-2 gap-4">
              {connectorTypes.map((connector) => (
                <div
                  key={connector.id}
                  onClick={() => handleConnectorSelect(connector)}
                  className="p-4 border border-gray-700 rounded-lg hover:border-pink-500 hover:bg-gray-750 cursor-pointer transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    {connector.icon}
                    <div>
                      <h4 className="font-medium text-gray-100">
                        {connector.name}
                      </h4>
                      <p className="text-sm text-gray-400">
                        {connector.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Add Credentials */}
        {step === 2 && selectedConnector && (
          <div className="p-6">
            <div className="flex items-center space-x-3 mb-6">
              {selectedConnector.icon}
              <div>
                <h3 className="text-lg font-medium text-gray-100">
                  Configure {selectedConnector.name}
                </h3>
                <p className="text-sm text-gray-400">
                  Enter your connection details
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Name
                  </label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 focus:outline-none focus:ring-2 focus:ring-pink-500"
                    placeholder="My Database"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Description
                  </label>
                  <input
                    type="text"
                    value={form.description}
                    onChange={(e) =>
                      setForm({ ...form, description: e.target.value })
                    }
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 focus:outline-none focus:ring-2 focus:ring-pink-500"
                    placeholder="Optional description"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Host
                  </label>
                  <input
                    type="text"
                    value={form.host}
                    onChange={(e) => setForm({ ...form, host: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 focus:outline-none focus:ring-2 focus:ring-pink-500"
                    placeholder="localhost"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Port
                  </label>
                  <input
                    type="text"
                    value={form.port}
                    onChange={(e) => setForm({ ...form, port: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 focus:outline-none focus:ring-2 focus:ring-pink-500"
                    placeholder="5432"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Database
                </label>
                <input
                  type="text"
                  value={form.database}
                  onChange={(e) =>
                    setForm({ ...form, database: e.target.value })
                  }
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 focus:outline-none focus:ring-2 focus:ring-pink-500"
                  placeholder="mydb"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Username
                  </label>
                  <input
                    type="text"
                    value={form.username}
                    onChange={(e) =>
                      setForm({ ...form, username: e.target.value })
                    }
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 focus:outline-none focus:ring-2 focus:ring-pink-500"
                    placeholder="username"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={form.password}
                      onChange={(e) =>
                        setForm({ ...form, password: e.target.value })
                      }
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 focus:outline-none focus:ring-2 focus:ring-pink-500 pr-10"
                      placeholder="••••••••"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-100"
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="ssl"
                  checked={form.ssl}
                  onChange={(e) => setForm({ ...form, ssl: e.target.checked })}
                  className="w-4 h-4 text-pink-600 bg-gray-700 border-gray-600 rounded focus:ring-pink-500"
                />
                <label htmlFor="ssl" className="text-sm text-gray-300">
                  Use SSL connection
                </label>
              </div>
            </div>

            <div className="flex justify-between mt-6">
              <Button
                variant="outline"
                onClick={() => setStep(1)}
                className="border-gray-600 text-gray-300 hover:bg-gray-700"
              >
                Back
              </Button>
              <Button
                onClick={() => setStep(3)}
                disabled={!form.name || !form.host}
                className="bg-pink-400 hover:bg-pink-500 disabled:opacity-50"
              >
                Next
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Test Connection */}
        {step === 3 && (
          <div className="p-6">
            <div className="flex items-center space-x-3 mb-6">
              <TestTube className="h-6 w-6 text-pink-400" />
              <div>
                <h3 className="text-lg font-medium text-gray-100">
                  Test Connection
                </h3>
                <p className="text-sm text-gray-400">
                  Verify your connection settings
                </p>
              </div>
            </div>

            <div className="bg-gray-750 rounded-lg p-4 mb-6">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Host:</span>
                  <span className="text-gray-100">{form.host}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Port:</span>
                  <span className="text-gray-100">{form.port}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Database:</span>
                  <span className="text-gray-100">{form.database}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Username:</span>
                  <span className="text-gray-100">{form.username}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">SSL:</span>
                  <span className="text-gray-100">
                    {form.ssl ? "Enabled" : "Disabled"}
                  </span>
                </div>
              </div>
            </div>

            {testResult && (
              <div
                className={`p-4 rounded-lg mb-6 ${
                  testResult === "success"
                    ? "bg-green-900/30 border border-green-600/30"
                    : "bg-red-900/30 border border-red-600/30"
                }`}
              >
                <div className="flex items-center space-x-2">
                  {testResult === "success" ? (
                    <CheckCircle className="h-5 w-5 text-green-400" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-400" />
                  )}
                  <span
                    className={
                      testResult === "success"
                        ? "text-green-400"
                        : "text-red-400"
                    }
                  >
                    {testMessage}
                  </span>
                </div>
              </div>
            )}

            <div className="flex justify-between">
              <Button
                variant="outline"
                onClick={() => setStep(2)}
                className="border-gray-600 text-gray-300 hover:bg-gray-700"
              >
                Back
              </Button>
              <div className="flex space-x-2">
                <Button
                  onClick={handleTestConnection}
                  disabled={isTesting}
                  className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                >
                  {isTesting ? (
                    <>
                      <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                      Testing...
                    </>
                  ) : (
                    <>
                      <TestTube className="h-4 w-4 mr-2" />
                      Test Connection
                    </>
                  )}
                </Button>
                {testResult === "success" && (
                  <Button
                    onClick={() => setStep(4)}
                    className="bg-pink-400 hover:bg-pink-500"
                  >
                    Continue
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Success */}
        {step === 4 && (
          <div className="p-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-green-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-100 mb-2">
                Connection Successful!
              </h3>
              <p className="text-gray-400 mb-6">
                Your data source "{form.name}" has been successfully connected
                and is ready to use.
              </p>
              <Button
                onClick={handleFinish}
                className="bg-pink-400 hover:bg-pink-500"
              >
                Done
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const DataSourcesPage: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [dataSources, setDataSources] =
    useState<DataSource[]>(sampleDataSources);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const itemsPerPage = 10;

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  // Filter data sources based on current filters
  let filteredDataSources = dataSources.filter((dataSource) => {
    const matchesStatus =
      statusFilter === "all" || dataSource.status === statusFilter;
    const matchesType = typeFilter === "all" || dataSource.type === typeFilter;
    const matchesSearch =
      searchQuery === "" ||
      dataSource.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      dataSource.description.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesStatus && matchesType && matchesSearch;
  });

  // Sort by last sync time (most recent first)
  filteredDataSources = filteredDataSources.sort(
    (a, b) => b.lastSync.getTime() - a.lastSync.getTime(),
  );

  // Calculate pagination
  const totalPages = Math.ceil(filteredDataSources.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentDataSources = filteredDataSources.slice(startIndex, endIndex);

  const handleAddDataSource = (newDataSource: DataSource) => {
    setDataSources([newDataSource, ...dataSources]);
  };

  return (
    <div className="flex h-screen bg-gray-900">
      {/* Mobile Sidebar Overlay */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar */}
      <div
        className={cn(
          "fixed lg:relative z-50 h-full transition-transform duration-300 ease-in-out",
          isSidebarOpen
            ? "translate-x-0"
            : "-translate-x-full lg:translate-x-0",
        )}
      >
        <Sidebar />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col h-full min-h-0 overflow-hidden">
        {/* Top Header */}
        <header className="bg-gray-800 border-b border-gray-700 px-4 lg:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {/* Mobile Menu Button */}
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden text-gray-300 hover:bg-gray-700"
                onClick={toggleSidebar}
              >
                <Menu className="h-5 w-5" />
              </Button>

              <h1 className="text-xl lg:text-2xl font-bold text-gray-100">
                Data Sources
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              <Button
                className="flex items-center space-x-2 bg-pink-400 hover:bg-pink-500"
                onClick={() => setIsAddModalOpen(true)}
              >
                <Plus className="h-4 w-4" />
                <span className="hidden sm:inline">Add Source</span>
              </Button>
              <Button
                variant="default"
                size="icon"
                className="bg-gray-700 hover:bg-gray-600 text-gray-300"
              >
                <Bell className="h-5 w-5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="text-gray-300 hover:bg-gray-700"
              >
                <Settings className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </header>

        {/* Filters */}
        <div className="bg-gray-800 border-b border-gray-700 px-4 lg:px-6 py-3">
          <div className="flex flex-col lg:flex-row lg:items-center space-y-3 lg:space-y-0 lg:space-x-4">
            <div className="flex items-center space-x-2">
              <Filter className="h-4 w-4 text-gray-400" />
              <span className="text-sm text-gray-300">Filters:</span>
            </div>

            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
              {/* Status Filter */}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-1 bg-gray-700 border border-gray-600 rounded-md text-sm text-gray-100 focus:outline-none focus:ring-2 focus:ring-pink-500"
              >
                <option value="all">All Status</option>
                <option value="connected">Connected</option>
                <option value="disconnected">Disconnected</option>
                <option value="error">Error</option>
                <option value="syncing">Syncing</option>
              </select>

              {/* Type Filter */}
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="px-3 py-1 bg-gray-700 border border-gray-600 rounded-md text-sm text-gray-100 focus:outline-none focus:ring-2 focus:ring-pink-500"
              >
                <option value="all">All Types</option>
                <option value="database">Database</option>
                <option value="api">API</option>
                <option value="file">File</option>
                <option value="stream">Stream</option>
                <option value="cache">Cache</option>
              </select>
            </div>

            <span className="text-sm text-gray-400">
              {filteredDataSources.length} of {dataSources.length} sources
            </span>
            <div className="relative lg:ml-auto">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-1 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 text-gray-100 placeholder-gray-400 w-full lg:w-auto"
              />
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <main className="flex-1 h-full flex flex-col min-h-0 bg-gray-900">
          {/* Data Sources List with Pagination */}
          <div className="h-full flex flex-col min-h-0">
            {/* Scrollable content area */}
            <div className="flex-1 p-4 lg:p-6 overflow-auto min-h-0">
              <div className="space-y-4">
                {currentDataSources.map((dataSource) => (
                  <DataSourceRow key={dataSource.id} dataSource={dataSource} />
                ))}
              </div>
            </div>

            {/* Pagination - Always visible at bottom */}
            {totalPages > 1 && (
              <div className="border-t border-gray-700 bg-gray-800 px-4 lg:px-6 py-4 flex-shrink-0">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                  <div className="text-sm text-gray-400">
                    Showing {startIndex + 1} to{" "}
                    {Math.min(endIndex, filteredDataSources.length)} of{" "}
                    {filteredDataSources.length} results
                  </div>

                  <div className="flex items-center justify-center sm:justify-end space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                      className="border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-50"
                    >
                      <ChevronsLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-50"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>

                    <div className="flex items-center space-x-1">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                        (page) => (
                          <Button
                            key={page}
                            variant={
                              page === currentPage ? "default" : "outline"
                            }
                            size="sm"
                            onClick={() => setCurrentPage(page)}
                            className={
                              page === currentPage
                                ? "bg-pink-400 hover:bg-pink-500 text-white"
                                : "border-gray-600 text-gray-300 hover:bg-gray-700"
                            }
                          >
                            {page}
                          </Button>
                        ),
                      )}
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-50"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages}
                      className="border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-50"
                    >
                      <ChevronsRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Add Source Modal */}
      {isAddModalOpen && (
        <AddSourceModal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          onSuccess={handleAddDataSource}
        />
      )}
    </div>
  );
};
